import { useEffect, useMemo, useRef } from 'react';
import { setDefaultOptions } from 'date-fns';
import Popover from '@mui/material/Popover';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { isDateString } from '@witmetrics/utils/strings';
import { hasKey } from '@witmetrics/utils/objects';
import {
  buildHoursArray,
  buildMinutesArray,
  formatDateWithTime,
  formatTextDate,
} from '@witmetrics/utils/time';
import {
  useDateFormatPreference,
  useTimeFormatPreference,
  useWeekStartsOnPreference,
} from '@witmetrics/data-store';
import { useToggle } from '@/hooks/useToggle';
import Button from '@/components/Button';
import TimePicker from '@/components/inputs/TimePicker';
import PresetsList from './PresetsList';
import InputField from './InputField';
import TimeInput from './TimeInput';
import type { DateTimePreset } from './types';
import { DEADLINE_PRESETS } from './constants';
import { classes, styles } from './utils';

export type { DateTimePreset };
export { DEADLINE_PRESETS };

export type DateTimeInputProps = {
  className?: string;
  disablePast?: boolean;
  showTime?: boolean;
  autoSet?: boolean; // Automatically set to 24 hours from now if no value is provided
  closeOnSelect?: boolean;
  value?: string | null;
  label: string;
  presets?: DateTimePreset[];
  onChange: (value: string | null) => void;
  // eslint-disable-next-line witmetrics/no-multiline-comments
  /* Pass any function (e.g. `() => null`) to enable the Cancel/Apply footer.
     Apply closes the popover and invokes this with the current value; Cancel
     reverts to the value the popover was opened with and closes.
   */
  onApply?: (value: string | null) => void;
};

export default function DateTimeInput({
  className,
  disablePast = true,
  showTime = true,
  autoSet = true,
  closeOnSelect = false,
  value,
  label,
  presets,
  onChange,
  onApply,
}: DateTimeInputProps) {
  const inputRef = useRef<HTMLDivElement>(null);
  const initialValueRef = useRef<string | null | undefined>(value);
  const dateFormat = useDateFormatPreference();
  const timeFormat = useTimeFormatPreference();
  const weekStartsOn = useWeekStartsOnPreference();
  const dateValue = useMemo(
    () => (isDateString(value) ? new Date(value) : undefined),
    [value]
  );
  const [isOpen, toggleOpen] = useToggle();

  useEffect(() => {
    setDefaultOptions({ weekStartsOn });
  }, [weekStartsOn]);

  useEffect(() => {
    if (isOpen) initialValueRef.current = value;
  }, [isOpen]);

  useEffect(() => {
    // Default to 24 hours from now
    if (autoSet && !isDateString(value)) {
      let time = new Date();
      time.setDate(time.getDate() + 1);
      // Ignore seconds/ms
      time.setSeconds(0, 0);
      // Round to the nearest increment of 5
      if (showTime) {
        time.setMinutes(Math.round(time.getMinutes() / 5) * 5);
      }
      onChange(time.toISOString());
    }
  }, [value]);

  const handleSelectDate = (val: Date) => {
    let time = dateValue ? new Date(dateValue) : new Date();
    time.setFullYear(val.getFullYear(), val.getMonth(), val.getDate());
    onChange(time.toISOString());
    if (closeOnSelect) toggleOpen(false);
  };

  const handleSelectTime = (update: { hours?: number; minutes?: number }) => {
    let time = dateValue ? new Date(dateValue) : new Date();
    if (hasKey(update, 'hours')) time.setHours(update.hours!);
    if (hasKey(update, 'minutes')) time.setMinutes(update.minutes!);
    onChange(time.toISOString());
    if (closeOnSelect) toggleOpen(false);
  };

  const handleSelectPreset = (preset: DateTimePreset) => {
    onChange(preset.getValue());
    if (closeOnSelect) toggleOpen(false);
  };

  const handleApply = () => {
    onApply?.(value ?? null);
    toggleOpen(false);
  };

  const handleCancel = () => {
    onChange(initialValueRef.current ?? null);
    toggleOpen(false);
  };

  const renderInputValue = () => {
    if (!value) return '';
    return showTime
      ? formatDateWithTime(value, dateFormat, timeFormat)
      : formatTextDate(value, dateFormat);
  };

  return (
    <>
      <InputField
        ref={inputRef}
        isOpen={isOpen}
        hasValue={!!value}
        className={className}
        label={label}
        value={renderInputValue()}
        onClear={() => onChange(null)}
        onClick={() => toggleOpen()}
      />
      <Popover
        open={isOpen}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        anchorEl={inputRef.current}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        slotProps={{ paper: { className: classes.paper } }}
        onClose={() => toggleOpen(false)}>
        <div className={classes.body}>
          {presets && (
            <PresetsList presets={presets} onSelect={handleSelectPreset} />
          )}
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateCalendar
              showDaysOutsideCurrentMonth
              disablePast={disablePast}
              sx={styles.calendar}
              value={dateValue || null}
              onChange={handleSelectDate}
            />
            {showTime && (
              <div className={classes.timeSection}>
                <TimeInput value={dateValue} onChange={handleSelectTime} />
                <TimePicker
                  showTitles={false}
                  hours={dateValue?.getHours()}
                  minutes={dateValue?.getMinutes()}
                  hoursOptions={buildHoursArray(true)}
                  minutesOptions={buildMinutesArray(5)}
                  onChange={handleSelectTime}
                />
              </div>
            )}
          </LocalizationProvider>
        </div>
        {onApply && (
          <div className={classes.footer}>
            <Button
              className={classes.cancelButton}
              variant="ghost"
              onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={handleApply}>Apply</Button>
          </div>
        )}
      </Popover>
    </>
  );
}
